/**
 * Simple, clean, modern placeholder images for listings without user-uploaded images
 * Uses SVG data URIs for instant loading - one placeholder per main listing type
 */

// Tailwind color values
const COLORS = {
  // Background colors (bg-*-50)
  blue50: '#eff6ff',
  green50: '#f0fdf4', 
  purple50: '#faf5ff',
  amber50: '#fffbeb',
  
  // Icon colors (darker shades for contrast)
  blue600: '#2563eb',
  green600: '#16a34a',
  purple600: '#9333ea',
  amber600: '#d97706',
};

/**
 * Generate SVG placeholder for Business listings
 * Office building icon on blue-50 background
 */
const generateBusinessPlaceholder = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
    <rect width="800" height="600" fill="${COLORS.blue50}"/>
    <g transform="translate(400, 300)" fill="none" stroke="${COLORS.blue600}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <!-- Office building icon -->
      <rect x="-50" y="-70" width="100" height="130" rx="4" />
      <line x1="-50" y1="60" x2="-70" y2="60" />
      <line x1="50" y1="60" x2="70" y2="60" />
      <!-- Windows row 1 -->
      <rect x="-35" y="-55" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <rect x="-9" y="-55" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <rect x="17" y="-55" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <!-- Windows row 2 -->
      <rect x="-35" y="-30" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <rect x="-9" y="-30" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <rect x="17" y="-30" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <!-- Windows row 3 -->
      <rect x="-35" y="-5" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <rect x="-9" y="-5" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <rect x="17" y="-5" width="18" height="14" rx="2" fill="${COLORS.blue600}" />
      <!-- Door -->
      <rect x="-12" y="25" width="24" height="35" rx="2" />
    </g>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Generate SVG placeholder for Job listings
 * Simple briefcase icon on green-50 background
 */
const generateJobPlaceholder = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
    <rect width="800" height="600" fill="${COLORS.green50}"/>
    <g transform="translate(400, 300)" fill="none" stroke="${COLORS.green600}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <!-- Briefcase icon -->
      <rect x="-70" y="-30" width="140" height="90" rx="8" />
      <path d="M-25 -30 L-25 -50 C-25 -60 -15 -70 0 -70 C15 -70 25 -60 25 -50 L25 -30" />
      <line x1="-70" y1="10" x2="70" y2="10" />
      <circle cx="0" cy="10" r="8" fill="${COLORS.green600}" />
    </g>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Generate SVG placeholder for Event listings
 * Simple calendar icon on purple-50 background
 */
const generateEventPlaceholder = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
    <rect width="800" height="600" fill="${COLORS.purple50}"/>
    <g transform="translate(400, 300)" fill="none" stroke="${COLORS.purple600}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <!-- Calendar icon -->
      <rect x="-60" y="-50" width="120" height="110" rx="8" />
      <line x1="-60" y1="-20" x2="60" y2="-20" />
      <line x1="-30" y1="-70" x2="-30" y2="-40" />
      <line x1="30" y1="-70" x2="30" y2="-40" />
      <!-- Calendar dots/dates -->
      <circle cx="-30" cy="10" r="6" fill="${COLORS.purple600}" />
      <circle cx="0" cy="10" r="6" fill="${COLORS.purple600}" />
      <circle cx="30" cy="10" r="6" fill="${COLORS.purple600}" />
      <circle cx="-30" cy="40" r="6" fill="${COLORS.purple600}" />
      <circle cx="0" cy="40" r="6" fill="${COLORS.purple600}" />
    </g>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

/**
 * Generate SVG placeholder for Classified listings
 * Simple price tag icon on amber-50 background
 */
const generateClassifiedPlaceholder = () => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600">
    <rect width="800" height="600" fill="${COLORS.amber50}"/>
    <g transform="translate(400, 300)" fill="none" stroke="${COLORS.amber600}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
      <!-- Price tag icon -->
      <path d="M-50 -60 L30 -60 L70 -20 L70 20 L30 60 L-50 60 Z" />
      <circle cx="-20" cy="0" r="12" fill="${COLORS.amber600}" />
      <line x1="10" y1="-25" x2="45" y2="10" />
      <line x1="10" y1="0" x2="35" y2="25" />
    </g>
  </svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Pre-generated placeholder data URIs
const PLACEHOLDERS = {
  business: generateBusinessPlaceholder(),
  job: generateJobPlaceholder(),
  event: generateEventPlaceholder(),
  classified: generateClassifiedPlaceholder(),
};

/**
 * Check if a given image URL is a placeholder image
 * @param {string} imageUrl - The image URL to check
 * @returns {boolean} True if the image is a placeholder
 */
export const isPlaceholderImage = (imageUrl) => {
  if (!imageUrl) return true;
  
  // Check if it's a data URI (SVG placeholder)
  if (imageUrl.startsWith('data:image/svg+xml')) return true;
  
  // Legacy: Check if it's from Unsplash (old placeholder source)
  if (imageUrl.includes('unsplash.com')) return true;
  
  // Legacy: Check if it's our old custom classified placeholder
  if (imageUrl.includes('static.prod-images.emergentagent.com')) return true;
  
  return false;
};

/**
 * Get placeholder image URL for a listing based on its type
 * @param {string} listingType - business, job, event, or classified
 * @param {string} category - Ignored (kept for backwards compatibility)
 * @returns {string} Data URI of the placeholder SVG
 */
export const getPlaceholderImage = (listingType, category) => {
  // Return the simple placeholder for the listing type
  return PLACEHOLDERS[listingType] || PLACEHOLDERS.business;
};

// Legacy exports for backwards compatibility
export const getJobPlaceholder = () => PLACEHOLDERS.job;
export const BUSINESS_PLACEHOLDERS = {};
export const JOB_PLACEHOLDERS = {};
export const EVENT_PLACEHOLDERS = {};
export const CLASSIFIED_PLACEHOLDERS = {};
export const DEFAULT_PLACEHOLDERS = PLACEHOLDERS;
